import { useState, useEffect } from "react";
import PageHeader from "../../components/pageHeader";
import AdminAttendanceTabs from "./adminAttendanceTabs";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import AttendanceList from "../../components/attendance/attendanceList";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";

export default function AttendanceHistory() {
  const token = sessionStorage.getItem("access_token");
  const { showToast, ToastContainer } = useToast();

  const [loading, setLoading] = useState(false);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [page, setPage] = useState(0);
  const limit = 8;

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() + i);

  // ======================================================
  // LOAD EVENTS
  // ======================================================
  useEffect(() => {
    fetchEvents();
  }, [selectedMonth, selectedYear]);

  async function fetchEvents() {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/attendance/history?month=${selectedMonth}&year=${selectedYear}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();

      if (data.success) {
        setEvents(data.items);
        setSelectedEvent(null); // reset selected event
        setPage(0);
      } else {
        showToast(data.message || "Failed to load history", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to load history", "error");
    }
    setLoading(false);
  }

  // Pagination logic
  const totalPages = Math.ceil(events.length / limit);
  const paginatedEvents = events.slice(page * limit, page * limit + limit);

  return (
    <MainContentLayout>
      <PageHeader title="Attendance History" backTo="/dashboard" />
      <AdminAttendanceTabs />

      {/* Filters */}
      <div className="flex space-x-2 mb-6">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="border rounded px-3 py-2"
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleString("default", { month: "long" })}
            </option>
          ))}
        </select>

        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="border rounded px-3 py-2"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Events */}
      {paginatedEvents.length === 0 ? (
        <p className="text-gray-500">No events found for this month.</p>
      ) : (
        <ul className="divide-y">
          {paginatedEvents.map((ev, idx) => {
            const d = new Date(ev.startAt);
            const isSelected = selectedEvent === ev.id;

            return (
              <li
                key={ev.id}
                className={`py-2 flex justify-between items-center cursor-pointer transition ${
                  isSelected ? "bg-blue-100 font-semibold" : "hover:bg-gray-50"
                }`}
              >
                <span>{idx + 1}. {d.toLocaleString()}</span>

                <button
                  className={`px-2 py-1 rounded text-sm ${
                    isSelected
                      ? "bg-blue-600 text-white"
                      : "text-blue-600 hover:underline"
                  }`}
                  onClick={() => setSelectedEvent(ev.id)}
                >
                  View Attendance
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <button
            className="px-3 py-1 border rounded disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <span>
            Page {page + 1} / {totalPages}
          </span>
          <button
            className="px-3 py-1 border rounded disabled:opacity-40"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Attendance List */}
      {selectedEvent && (
        <div className="mt-6">
          <h1 className="mb-2">Attendances</h1>
          <AttendanceList eventId={selectedEvent} />
        </div>
      )}

      <ToastContainer />
      <LoadingOverlay show={loading} text="Loading..." />
    </MainContentLayout>
  );
}
